import { IJournalCategory } from '../../../../global/entities/Journal/Journal.ts';

export interface ConfigFormValues {
    conditionJson: string;
    conditionCategory: string | null;
    conditionQuestionId: string | null;
    conditionOperator: string;
    conditionThreshold: number | string;
    conditionTriggerValues: string[];
    conditionKeywords: string[];
    conditionUnitId: number | null;
    conditionThresholdDays: number;
    conditionMaxReminders: number;
    severity: string;
    deliveryChannels: string;
    escalationEnabled: boolean;
    escalationDelayMinutes: number | null;
    escalationTargetType: string;
    escalationTargetID: number | null;
    escalationEmailRecipients: string;
    escalationSmsRecipients: string;
    level2EscalationEnabled: boolean;
    level2EscalationDelayMinutes: number | null;
    level2EscalationTargetType: string;
    level2EscalationTargetID: number | null;
    level2EscalationEmailRecipients: string;
    level2EscalationSmsRecipients: string;
    isEnabled: boolean;
    healthcareFacilityID: number | null;
    sourceEntityID: number | null;
    notificationEnabled: boolean;
    notificationHcpID: number | null;
    notifyByEmail: boolean;
    notifyBySMS: boolean;
    escalationNotifyByEmail: boolean;
    escalationNotifyBySMS: boolean;
    level2EscalationNotifyByEmail: boolean;
    level2EscalationNotifyBySMS: boolean;
    notifyPatientBySMS: boolean;
    conditionEvaluationMode: string;
    // Trend fields (applies when conditionEvaluationMode === 'trend')
    trendType: string;
    trendDeltaThreshold: number;
    trendMinConsecutiveIncreases: number;
    trendMeanDeltaThreshold: number;
    trendWindowSize: number;
    trendMinCurrentScore: number;
    trendMissingDataPolicy: string;
    // Physiological trend fields (kept for physiological alerts)
    conditionTrendType: string | null;
    conditionSampleCount: number;
    conditionTrendValue: number | string;
    conditionValueMode: string;
}

export const TREND_TYPE_SUDDEN_INCREASE = 'SUDDEN_INCREASE';
export const TREND_TYPE_CONSECUTIVE_INCREASE = 'CONSECUTIVE_INCREASE';
export const TREND_TYPE_ABOVE_AVERAGE = 'ABOVE_AVERAGE';

export const defaultConfigValues: ConfigFormValues = {
    conditionJson: '{}',
    conditionCategory: null,
    conditionQuestionId: null,
    conditionOperator: '>=',
    conditionThreshold: '',
    conditionTriggerValues: [],
    conditionKeywords: [],
    conditionUnitId: null,
    conditionThresholdDays: 3,
    conditionMaxReminders: 3,
    severity: 'Moderate',
    deliveryChannels: 'InApp',
    escalationEnabled: false,
    escalationDelayMinutes: 7200,
    escalationTargetType: '',
    escalationTargetID: null,
    escalationEmailRecipients: '',
    escalationSmsRecipients: '',
    level2EscalationEnabled: false,
    level2EscalationDelayMinutes: 1440,
    level2EscalationTargetType: '',
    level2EscalationTargetID: null,
    level2EscalationEmailRecipients: '',
    level2EscalationSmsRecipients: '',
    isEnabled: true,
    healthcareFacilityID: null,
    sourceEntityID: null,
    notificationEnabled: false,
    notificationHcpID: null,
    notifyByEmail: false,
    notifyBySMS: false,
    escalationNotifyByEmail: false,
    escalationNotifyBySMS: false,
    level2EscalationNotifyByEmail: false,
    level2EscalationNotifyBySMS: false,
    notifyPatientBySMS: false,
    conditionEvaluationMode: 'threshold',
    // Trend defaults
    trendType: TREND_TYPE_SUDDEN_INCREASE,
    trendDeltaThreshold: 3,
    trendMinConsecutiveIncreases: 2,
    trendMeanDeltaThreshold: 2,
    trendWindowSize: 3,
    trendMinCurrentScore: 8,
    trendMissingDataPolicy: 'skip',
    // Physiological trend defaults
    conditionTrendType: null,
    conditionSampleCount: 3,
    conditionTrendValue: '',
    conditionValueMode: 'absolute',
};

export const operatorOptions = [
    { value: '>=', label: '>=' },
    { value: '>', label: '>' },
    { value: '<=', label: '<=' },
    { value: '<', label: '<' },
    { value: '==', label: '==' },
    { value: '!=', label: '!=' },
];

export const getSeverityOptions = (t: (key: string) => string) => [
    { value: 'Low', label: t('alert.severity.low') },
    { value: 'Moderate', label: t('alert.severity.moderate') },
    { value: 'High', label: t('alert.severity.high') },
];

export const severityColor = (severity: string) => {
    switch (severity) {
        case 'High': return 'orange';
        case 'Moderate': return 'yellow';
        case 'Low': return 'blue';
        default: return 'gray';
    }
};

export const trendTypeOptions = (t: (key: string) => string) => [
    { value: 'INCREASE', label: t('alertRule.trend.increase') },
    { value: 'DECREASE', label: t('alertRule.trend.decrease') },
    { value: 'ABOVE', label: t('alertRule.trend.above') },
    { value: 'BELOW', label: t('alertRule.trend.below') },
];

export const valueModeOptions = (t: (key: string) => string) => [
    { value: 'absolute', label: t('alertRule.trend.absolute') },
    { value: 'percentage', label: t('alertRule.trend.percentage') },
];

export const getQuestionType = (
    questionId: string | null,
    categories: IJournalCategory[] | undefined,
) => {
    if (!questionId || !categories) return null;
    for (const cat of categories) {
        const q = (cat.questions || []).find((q) => q.id.toString() === questionId);
        if (q) return q;
    }
    return null;
};
